import React, { useEffect, useState, useCallback, lazy, Suspense } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Radar, Search, RefreshCw, Plane, Clock, Lock } from "lucide-react";
import Seo from "../components/Seo";
import AirportPicker from "../components/AirportPicker";
import ProGate, { useIsPro } from "../components/ProGate";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { fmtNumber } from "../data/mock";

const FlightMap = lazy(() => import("../components/FlightMap"));
const REFRESH_MS = 10000;

function AircraftRow({ a, active, onClick }) {
  return (
    <button onClick={onClick} data-testid="aircraft-row" className={`flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${active ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-blue-300"}`}>
      <span className="min-w-0">
        <span className="block truncate text-sm font-bold text-slate-900">{a.callsign || a.registration || a.hex}{a.airlineIata && <span className="ml-1.5 rounded bg-slate-100 px-1 text-[10px] font-semibold text-slate-600">{a.airlineIata}</span>}</span>
        <span className="block truncate text-xs text-slate-500">{a.aircraftType || "Unknown type"} · {a.registration || "—"}</span>
      </span>
      <span className="shrink-0 text-right text-xs text-slate-600">
        <span className="block font-semibold text-slate-900">{a.onGround ? "On ground" : `${fmtNumber(a.altitudeFt)} ft`}</span>
        <span className="block">{Math.round(a.groundSpeedKts || 0)} kts · {Math.round(a.heading || 0)}°</span>
      </span>
    </button>
  );
}

function ScheduleCard({ flight }) {
  const [s, setS] = useState(null);
  useEffect(() => { setS(null); if (flight) api.trackerSchedule(flight).then(setS).catch(() => setS(null)); }, [flight]);
  if (!flight || !s) return null;
  if (!s.available) return <p className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-3 text-xs text-slate-500" data-testid="schedule-unavailable"><Clock className="mr-1 inline h-3.5 w-3.5" /> {s.reason}</p>;
  if (!s.flight) return <p className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-500">No schedule found for {flight} today.</p>;
  const f = s.flight;
  const hm = (iso) => (iso ? iso.slice(11, 16) : "—");
  const delay = f.arrival?.delay || f.departure?.delay || 0;
  const Leg = ({ label, leg }) => (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-base font-extrabold text-slate-900">{leg?.iata} <span className="text-xs font-medium text-slate-500">{leg?.airport}</span></p>
      <p className="text-xs text-slate-600">Sched {hm(leg?.scheduled)}{leg?.estimated && leg.estimated !== leg.scheduled ? ` · Est ${hm(leg.estimated)}` : ""}{leg?.actual ? ` · Actual ${hm(leg.actual)}` : ""}</p>
      <p className="text-xs text-slate-600">{leg?.terminal ? `Terminal ${leg.terminal}` : "Terminal —"}{leg?.gate ? ` · Gate ${leg.gate}` : ""}{leg?.baggage ? ` · Belt ${leg.baggage}` : ""}</p>
    </div>
  );
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm" data-testid="schedule-card">
      <div className="flex items-center justify-between">
        <p className="font-bold text-slate-900">{f.airline}</p>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold capitalize ${delay > 0 ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-700"}`} data-testid="schedule-status">{delay > 0 ? `Delayed ${delay} min` : f.status}</span>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2"><Leg label="Departure" leg={f.departure} /><Leg label="Arrival" leg={f.arrival} /></div>
      <p className="mt-2 text-[10px] text-slate-400">Times in UTC · schedule data by AviationStack</p>
    </div>
  );
}

const DEMO_AIRCRAFT = [{ hex: "d1", lat: 41.2, lon: -72.1, heading: 55, altitudeFt: 34000, groundSpeedKts: 480 }, { hex: "d2", lat: 40.2, lon: -74.6, heading: 230, altitudeFt: 12000, groundSpeedKts: 310 }, { hex: "d3", lat: 40.7, lon: -73.3, heading: 90, altitudeFt: 6000, groundSpeedKts: 240 }];

export default function Tracker() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const isPro = useIsPro();
  const [flight, setFlight] = useState(params.get("flight") || "");
  const [airport, setAirport] = useState(params.get("airport") || (params.get("flight") ? "" : "JFK"));
  const [airline, setAirline] = useState(params.get("airline") || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const mode = params.get("flight") ? "flight" : "airport";

  const load = useCallback(async () => {
    if (!isPro) return;
    setLoading(true); setError("");
    try {
      const res = mode === "flight" ? await api.trackFlight(params.get("flight")) : await api.trackAirport(params.get("airport") || "JFK", params.get("airline") || "");
      setData(res); setUpdatedAt(new Date());
    } catch (e) { setError(e?.response?.data?.detail || "Live data unavailable right now."); } finally { setLoading(false); }
  }, [isPro, mode, params]);

  useEffect(() => { load(); const t = setInterval(load, REFRESH_MS); return () => clearInterval(t); }, [load]);

  const searchFlight = () => { if (flight.trim()) setParams({ flight: flight.trim().toUpperCase() }); };
  const searchAirport = () => { if (airport) setParams({ airport, ...(airline ? { airline } : {}) }); };
  const aircraft = isPro ? data?.aircraft || [] : DEMO_AIRCRAFT;
  const center = isPro ? data?.airport : { lat: 40.64, lon: -73.78 };

  return (
    <main className="min-h-[70vh] bg-slate-50">
      <Seo title="Live Flight Tracker | FindMyFlight PRO" description="Track any flight live on a map — real-time position, altitude and speed from the global ADS-B network. A FindMyFlight PRO feature." path="/tracker" />
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-5 py-8">
          <div className="flex items-center gap-2 text-blue-600"><Radar className="h-5 w-5" /><span className="text-sm font-semibold uppercase tracking-wide">Flight Tracker</span><span className="rounded-md bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold text-white">PRO</span></div>
          <h1 className="mt-2 text-3xl font-extrabold text-slate-900">Watch your flight live</h1>
          <p className="mt-2 max-w-2xl text-slate-600">Real-time aircraft positions from the global ADS-B network, refreshed every 10 seconds. Search a flight number, or see everything in the air around an airport.</p>
          <div className="mt-6 grid gap-3 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Flight number</label>
              <div className="flex gap-2">
                <input value={flight} onChange={(e) => setFlight(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && searchFlight()} placeholder="e.g. BA117, UA1, DL400" data-testid="tracker-flight-input" className="h-11 flex-1 rounded-lg border border-slate-200 px-3 text-base font-semibold uppercase outline-none focus:border-blue-500 sm:text-sm" />
                <Button onClick={searchFlight} data-testid="tracker-flight-submit" className="h-11 gap-2 bg-blue-600 hover:bg-blue-700"><Search className="h-4 w-4" /> Track</Button>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <AirportPicker label="Live traffic near" value={airport} onChange={setAirport} testId="tracker-airport-input" className="flex-1" />
                <input value={airline} onChange={(e) => setAirline(e.target.value.toUpperCase().slice(0, 3))} placeholder="Airline (BA)" data-testid="tracker-airline-input" className="h-11 w-full rounded-lg border border-slate-200 px-3 text-base font-semibold uppercase outline-none focus:border-blue-500 sm:w-28 sm:text-sm" />
                <Button onClick={searchAirport} data-testid="tracker-airport-submit" variant="outline" className="h-11 gap-2"><Plane className="h-4 w-4" /> Show</Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-5 py-8">
        <ProGate locked={!isPro} title="Live tracking is a PRO feature" description={user ? "Upgrade to PRO to follow any flight in real time — live position, altitude, speed and heading from the global ADS-B network, plus a full year of award availability, Cabin Compare and alerts." : "Sign in and go PRO to follow any flight in real time — live position, altitude, speed and heading from the global ADS-B network."}>
          <div className="grid gap-5 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <Suspense fallback={<div className="h-[360px] animate-pulse rounded-2xl bg-slate-200 sm:h-[480px]" />}>
                <FlightMap aircraft={aircraft} center={center} radiusNm={mode === "airport" ? data?.radiusNm || 60 : null} selected={selected} onSelect={setSelected} />
              </Suspense>
              <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                <span data-testid="tracker-status">{loading ? "Updating…" : updatedAt ? `Updated ${updatedAt.toLocaleTimeString()} · auto-refreshes every 10s` : isPro ? "" : "Preview"}</span>
                <button onClick={load} className="flex items-center gap-1 font-semibold text-blue-600 hover:underline" data-testid="tracker-refresh"><RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh</button>
              </div>
            </div>
            <div className="space-y-2">
              {mode === "flight" && <ScheduleCard flight={params.get("flight")} />}
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold text-slate-900" data-testid="tracker-results-heading">{mode === "flight" ? `Aircraft flying ${params.get("flight")}` : `In the air near ${data?.airport?.code || params.get("airport") || "JFK"}${params.get("airline") ? ` · ${params.get("airline")}` : ""}`}</h2>
                <span className="text-xs text-slate-500">{aircraft.length} found</span>
              </div>
              {error && <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="tracker-error">{error}</p>}
              {isPro && !loading && !error && aircraft.length === 0 && (
                <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-500" data-testid="tracker-empty">
                  {mode === "flight" ? <>No aircraft is currently broadcasting <b>{params.get("flight")}</b>. It may not have departed yet — try again closer to departure, or check live traffic near the departure airport.</> : "Nothing in range right now."}
                </div>
              )}
              <div className="max-h-[420px] space-y-2 overflow-auto pr-1">
                {aircraft.slice(0, 40).map((a) => <AircraftRow key={a.hex} a={a} active={selected?.hex === a.hex} onClick={() => setSelected(a)} />)}
              </div>
            </div>
          </div>
        </ProGate>
        {!isPro && <p className="mt-6 text-center text-sm text-slate-500">Already PRO? <Link to="/login" className="font-semibold text-blue-600 hover:underline">Sign in</Link> to start tracking.</p>}
        <p className="mt-4 text-center text-xs text-slate-400 flex items-center justify-center gap-1"><Lock className="h-3 w-3" /> Positions come from crowdsourced ADS-B receivers; coverage over oceans and remote areas can be limited.</p>
      </div>
    </main>
  );
}
